import type { AgentSessionInfo, ChatSession } from "../../../src/types/index.ts";

function sessionTimestamp(updatedAt?: string): number {
  const parsed = updatedAt ? Date.parse(updatedAt) : NaN;
  return Number.isFinite(parsed) ? parsed : Date.now();
}

function sortSessionsByRecent(sessions: ChatSession[]): ChatSession[] {
  return [...sessions].sort((a, b) => b.lastMessageAt - a.lastMessageAt);
}

/**
 * Fold the agent's just-listed sessions into the locally-known ones.
 *
 * `agent.session.list` only ever reflects the runtime's current project/cwd, so
 * the discovery response never covers sessions that belong to other projects.
 * We therefore MERGE by session id and RETAIN every locally-known session the
 * response does not mention — replacing the list wholesale would erase other
 * projects' sessions from the sidebar the moment the user switched project.
 */
export function mergeDiscoveredSessions(
  existing: ChatSession[],
  discovered: AgentSessionInfo[],
  fallbackAgentId: string | null,
  fallbackFolder: string | null,
): ChatSession[] {
  const existingByAcpId = new Map<string, ChatSession>();
  for (const session of existing) {
    if (session.acpSessionId) {
      existingByAcpId.set(session.acpSessionId, session);
    }
  }

  const validDiscovered = discovered.filter((found): found is AgentSessionInfo => {
    return Boolean(found && typeof found.sessionId === "string" && found.sessionId.trim());
  });

  if (validDiscovered.length === 0) {
    return existing;
  }

  // Track which existing sessions the discovery response updates so the ones it
  // omits (other projects' sessions) are retained untouched.
  const updatedExistingIds = new Set<string>();
  let changed = false;

  const discoveredSessions: ChatSession[] = validDiscovered.map((found) => {
    const local = existingByAcpId.get(found.sessionId);
    const nextFolder = found.cwd || local?.projectFolder || fallbackFolder || "Unknown";
    const nextTitle = found.title?.trim() || local?.title || `Session ${found.sessionId.slice(0, 8)}`;
    const nextTimestamp = sessionTimestamp(found.updatedAt);

    if (local) {
      updatedExistingIds.add(local.id);
      const nextLastMessageAt = Math.max(local.lastMessageAt, nextTimestamp);
      const merged: ChatSession = {
        ...local,
        acpSessionId: found.sessionId,
        title: nextTitle,
        projectFolder: nextFolder,
        lastMessageAt: nextLastMessageAt,
      };
      if (
        merged.title !== local.title
        || merged.projectFolder !== local.projectFolder
        || merged.lastMessageAt !== local.lastMessageAt
        || merged.acpSessionId !== local.acpSessionId
      ) {
        changed = true;
      }
      return merged;
    }

    changed = true;
    return {
      id: `acp:${found.sessionId}`,
      acpSessionId: found.sessionId,
      title: nextTitle,
      projectFolder: nextFolder,
      agentId: fallbackAgentId || "unknown",
      messageCount: 0,
      lastMessageAt: nextTimestamp,
      createdAt: nextTimestamp,
    };
  });

  // Keep every locally-known session the current discovery response did not
  // cover — those belong to other projects/cwds the runtime is not attached to.
  // Guard against id collisions with freshly-created discovered sessions so a
  // session can never appear twice in the sidebar.
  const discoveredIds = new Set(discoveredSessions.map((session) => session.id));
  const retained = existing.filter(
    (session) => !updatedExistingIds.has(session.id) && !discoveredIds.has(session.id),
  );

  if (!changed) {
    return existing;
  }

  return sortSessionsByRecent([...discoveredSessions, ...retained]);
}
